import { ApiError } from './legacy-client.js';

const TLS_CODES = new Set([
  'CERT_HAS_EXPIRED',
  'UNABLE_TO_VERIFY_LEAF_SIGNATURE',
  'SELF_SIGNED_CERT_IN_CHAIN',
  'CERT_SIGNATURE_FAILURE',
  'DEPTH_ZERO_SELF_SIGNED_CERT',
]);

/**
 * Translate a Node.js fetch-layer error into a typed `ApiError`. Returns
 * the input unchanged if it's not a recognized fetch failure.
 */
export function translateFetchError(err: unknown): Error {
  if (!(err instanceof Error)) return new Error(String(err));
  if (err.name === 'AbortError') {
    return new ApiError(0, 'FETCH_TIMEOUT', 'Request timed out before the server responded');
  }
  if (err instanceof TypeError) {
    const causeCode = (err as any).cause?.code;
    if (causeCode === 'ECONNREFUSED') {
      return new ApiError(0, 'CONNECTION_REFUSED', 'Server refused the connection');
    }
    if (causeCode === 'ENOTFOUND') {
      return new ApiError(0, 'DNS_FAILURE', 'DNS lookup failed for the API host');
    }
    if (causeCode && TLS_CODES.has(causeCode)) {
      return new ApiError(0, 'TLS_ERROR', `TLS handshake failed: ${causeCode}`);
    }
    return new ApiError(0, 'NETWORK_ERROR', err.message);
  }
  return err;
}

// Cause codes Node's fetch (undici) attaches when the request never reaches a
// server. Kept narrow so a genuine CLI bug that happens to be a TypeError is
// not misreported as "check your connection".
const NETWORK_CAUSE_CODES = new Set([
  'ECONNREFUSED',
  'ENOTFOUND',
  'ECONNRESET',
  'ETIMEDOUT',
  'EAI_AGAIN',
  'UND_ERR_CONNECT_TIMEOUT',
  'UND_ERR_SOCKET',
]);

/**
 * Turn a raw fetch-layer failure into a blameless, actionable line for the
 * top-level error handlers. Returns null when the error is not a recognised
 * connectivity failure, so callers fall back to their normal rendering and a
 * real bug is never masked as a network problem. §23b: errors are plain,
 * blameless, actionable.
 */
export function friendlyNetworkMessage(err: unknown): string | null {
  if (!(err instanceof Error)) return null;
  const causeCode = (err as { cause?: { code?: string } }).cause?.code;
  const isFetchFailure =
    err.name === 'AbortError' ||
    err.message === 'fetch failed' ||
    (causeCode !== undefined && NETWORK_CAUSE_CODES.has(causeCode));
  if (!isFetchFailure) return null;

  if (err.name === 'AbortError') {
    return "CloudGrid didn't respond in time.\n  Check your connection and try again.";
  }
  if (causeCode === 'ENOTFOUND' || causeCode === 'EAI_AGAIN') {
    return "Can't reach CloudGrid — the API host didn't resolve.\n  Check your connection, then try again.";
  }
  if (causeCode === 'ECONNREFUSED') {
    return "Can't reach CloudGrid — the connection was refused.\n  If you set a custom api_url, check it. Otherwise try again shortly.";
  }
  return "Can't reach CloudGrid.\n  Check your internet connection and try again.";
}
